import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Clock, ArrowRight } from 'lucide-react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase-config';
import { calculateDealScore, getDealBadge } from '../services/aiTransformService';

/**
 * Top 5 Projects Weekly Showcase
 * Steve's strategy: Weekly top 5 best deals sent to investors
 */
export default function Top5ProjectsWeekly() {
  const [topProjects, setTopProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTop5Projects();
  }, []);

  const loadTop5Projects = async () => {
    setLoading(true);
    try {
      // Get all published projects from last 7 days
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const q = query(
        collection(db, 'ai_properties'),
        where('status', '==', 'published'),
        where('createdAt', '>=', oneWeekAgo),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const snapshot = await getDocs(q);
      const projectsData = snapshot.docs.map(doc => {
        const data = doc.data();
        const dealScore = calculateDealScore(data.propertyData, data.transformation.priceImpact);
        const badge = getDealBadge(dealScore, data.transformation.priceImpact.increase);
        
        return {
          id: doc.id,
          ...data,
          dealScore,
          badge
        };
      });

      // Sort by deal score and take top 5
      const top5 = projectsData
        .sort((a, b) => b.dealScore - a.dealScore)
        .slice(0, 5);

      setTopProjects(top5);
    } catch (error) {
      console.error('Error loading top 5 projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const ProjectRow = ({ project, rank }) => {
    const { propertyData, transformation, dealScore, badge } = project;
    const profit = transformation.priceImpact.increase;
    const profitMillions = (profit / 1000000).toFixed(1);
    const totalMillions = (transformation.priceImpact.originalPrice / 1000000).toFixed(0);
    const marketMillions = (transformation.priceImpact.newPrice / 1000000).toFixed(0);

    return (
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:border-secondary/50 transition-all duration-300 group">
        <div className="flex items-center gap-4">
          {/* Rank */}
          <div className="flex-shrink-0">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-xl ${
              rank === 1 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-black' :
              rank === 2 ? 'bg-gradient-to-br from-gray-300 to-gray-500 text-black' :
              rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-black' :
              'bg-white/10 text-white'
            }`}>
              #{rank}
            </div>
          </div>

          {/* Image */}
          <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden">
            <img 
              src={transformation.transformedImages[0] || '/placeholder-house.jpg'} 
              alt={propertyData.address}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-white font-bold truncate">{propertyData.address}</h3>
              {badge && (
                <span className={`px-2 py-0.5 ${badge.color} ${badge.textColor} text-xs font-bold rounded-full whitespace-nowrap`}>
                  {badge.label}
                </span>
              )}
            </div>
            <div className="text-gray-400 text-sm mb-2">
              {propertyData.area_m2} m² • {transformation.selectedStyle}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-gray-400">Költség: </span>
                <span className="text-white font-semibold">{totalMillions}M</span>
              </div>
              <div>
                <span className="text-gray-400">Piaci: </span>
                <span className="text-white font-semibold">{marketMillions}M</span>
              </div>
              <div>
                <span className="text-secondary font-bold">+{profitMillions}M</span>
              </div>
            </div>
          </div>

          {/* Score & CTA */}
          <div className="flex-shrink-0 text-right">
            <div className="text-yellow-400 font-black text-2xl mb-2">
              {dealScore}
            </div>
            <button className="px-4 py-2 bg-secondary hover:bg-red-600 text-white font-bold rounded-lg transition-all duration-300 flex items-center gap-2 group-hover:scale-105">
              Részletek
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-[#0a0a0a] py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/40 rounded-full text-white text-sm font-medium mb-6">
            <Trophy className="w-5 h-5 text-yellow-400" />
            HÉT TOP 5 PROJEKTJE
          </div>
          
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">
            🔥 Top 5 Ingatlan Projekt
          </h1>
          
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-6">
            A hét legjobb befektetési lehetőségei – <span className="font-bold text-secondary">limitált elérhetőség</span>
          </p>

          <div className="inline-flex items-center gap-2 text-gray-400 text-sm">
            <Clock className="w-4 h-4" />
            Frissítve: {new Date().toLocaleDateString('hu-HU')}
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-red-600/20 to-orange-600/20 border border-red-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-2">⚡ FONTOS</h3>
              <p className="text-gray-300 mb-2">
                Ezek a projektek <span className="font-bold text-white">limitáltak</span> – minden csak <span className="font-bold text-secondary">1 vevő/befektető</span> számára elérhető.
              </p>
              <p className="text-gray-400 text-sm">
                Ha érdekel valamelyik, <span className="font-semibold text-white">gyorsan lépj</span> – a legjobb dealek órák alatt elkelnek.
              </p>
            </div>
          </div>
        </div>

        {/* Top 5 List */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-secondary border-t-transparent"></div>
            <p className="text-gray-400 mt-4">Top projektek betöltése...</p>
          </div>
        ) : topProjects.length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">Még nincsenek projektek ezen a héten</p>
          </div>
        ) : (
          <div className="space-y-4">
            {topProjects.map((project, index) => (
              <ProjectRow key={project.id} project={project} rank={index + 1} />
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Szeretnéd elsőként látni a legjobb dealeket?
            </h3>
            <p className="text-gray-300 mb-6">
              Befektetői hozzáféréssel <span className="font-bold text-white">heti szinten</span> kapod a top projekteket – mielőtt mások látnák.
            </p>
            <button className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold text-lg rounded-lg transition-all duration-300 transform hover:scale-105">
              Befektetői Hozzáférés
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
